// Regras puras do Relatório de Tempo (27/10/2026) — testadas em regras.test.js.

const NUM1 = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 1 });

/** Minutos → "32 min", "1h 14min", "2h". Sem tempo → "—". */
export function formatarMinutos(min) {
  if (min == null || Number.isNaN(Number(min))) return "—";
  const total = Math.round(Number(min));
  if (total < 60) return `${total} min`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m ? `${h}h ${String(m).padStart(2, "0")}min` : `${h}h`;
}

/** 2.5 → "2,5"; null → "—". */
export const formatarDecimal = (v) => (v == null ? "—" : NUM1.format(Number(v)));

/**
 * ▲▼ de um número contra o período anterior. `tipo`: "pct" (variação em %),
 * "min" (diferença em minutos) ou "pontos" (diferença de uma porcentagem).
 * `melhor`: "mais", "menos" ou null (nem bom nem ruim, ex.: tempo de mesa).
 * Devolve { sentido: sobe|desce|igual, texto, bom: true|false|null } ou null sem base.
 */
export function variacao(atual, antes, { tipo = "pct", melhor = "mais" } = {}) {
  if (atual == null || antes == null) return null;
  const diff = Number(atual) - Number(antes);
  let valor;
  let texto;
  if (tipo === "pct") {
    if (!Number(antes)) return null;
    valor = (diff / Math.abs(Number(antes))) * 100;
    texto = `${Math.round(Math.abs(valor))}%`;
  } else if (tipo === "min") {
    valor = diff;
    texto = formatarMinutos(Math.abs(diff));
  } else {
    valor = diff;
    texto = `${Math.round(Math.abs(diff))} ${Math.round(Math.abs(diff)) === 1 ? "ponto" : "pontos"}`;
  }
  if (Math.abs(valor) < 0.5) return { sentido: "igual", texto: "igual", bom: null };
  const sentido = valor > 0 ? "sobe" : "desce";
  return { sentido, texto, bom: melhor ? (sentido === "sobe") === (melhor === "mais") : null };
}

/** Props do ▲▼ de um número do topo: comparar(chave, formatar, opções) → { variacao, antes }. */
export const comparador = (atual, anterior) => (chave, formatar, opcoes) => {
  if (!anterior) return {};
  const antes = anterior[chave];
  return { variacao: variacao(atual?.[chave], antes, opcoes), antes: antes == null ? null : formatar(antes) };
};

/**
 * Quanto a mesa rende por hora comparada com a média da casa:
 * "alto" (≥ 115% da média), "baixo" (≤ 85%) ou "medio". Sem número → null.
 */
export function rendimento(valor, mediaCasa) {
  if (valor == null || !mediaCasa) return null;
  const r = valor / mediaCasa;
  if (r >= 1.15) return "alto";
  if (r <= 0.85) return "baixo";
  return "medio";
}

/** Ordena uma lista pela coluna; vazio (null) sempre no fim. */
export function ordenar(lista, chave, direcao = "desc") {
  const sinal = direcao === "asc" ? 1 : -1;
  return [...lista].sort((a, b) => {
    const x = a[chave];
    const y = b[chave];
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    if (typeof x === "string") return sinal * x.localeCompare(y, "pt-BR", { numeric: true });
    return sinal * (x - y);
  });
}

/** A etapa que mais demora (o gargalo) entre as que têm tempo. */
export function gargalo(etapas) {
  const comTempo = (etapas || []).filter((e) => e.tempo_medio != null);
  if (!comTempo.length) return null;
  return comTempo.reduce((a, b) => (b.tempo_medio > a.tempo_medio ? b : a));
}

/**
 * Horas do dia que aparecem no mapa: da primeira à última com movimento de
 * verdade (pelo menos `corte` da hora mais forte — um pedido perdido à 0h não
 * abre seis colunas vazias), sem buracos no meio.
 */
export function horasDoMapa(horas, corte = 0.02) {
  if (!horas?.length) return [];
  const maior = Math.max(...horas.map((h) => Number(h.total) || 0));
  const fortes = horas.filter((h) => !maior || (Number(h.total) || 0) >= maior * corte);
  const nums = (fortes.length ? fortes : horas).map((h) => h.hora);
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const porHora = Object.fromEntries(horas.map((h) => [h.hora, h]));
  return Array.from({ length: max - min + 1 }, (_, i) => porHora[min + i] || { hora: min + i, rotulo: `${String(min + i).padStart(2, "0")}h` });
}

/** Meta de tempo por canal, em minutos: entrega em até 45, retirada em até 30. */
export const META_MINUTOS = { delivery: 45, ifood: 45, retirada: 30 };

/** % dos pedidos com tempo que ficaram em até `meta` minutos, pelas faixas (`ate` = teto da faixa; a última não tem). */
export function ateMeta(faixas, meta) {
  const total = (faixas || []).reduce((s, f) => s + f.vendas, 0);
  if (!total) return null;
  const dentro = faixas.filter((f) => f.ate != null && f.ate <= meta).reduce((s, f) => s + f.vendas, 0);
  return (dentro / total) * 100;
}

/** Eixo em reais curto: "R$ 850", "R$ 6k", "R$ 1,2k". */
export const reaisEixo = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) < 1000) return `R$ ${Math.round(n)}`;
  const k = n / 1000;
  return `R$ ${(Math.abs(k) >= 10 || Number.isInteger(k) ? Math.round(k) : k.toFixed(1)).toString().replace(".", ",")}k`;
};

// Faixas do mapa hora a hora, as mesmas da Visão Geral: % do pico da linha.
// Hora com menos de 5 pedidos não fica "alta" nem "mediana" (pouco movimento
// não é pico de verdade, só a melhor de poucas).
const MIN_PEDIDOS_FORTE = 5;
export const NOME_FAIXA = { alta: "alta", media: "mediana", baixa: "baixa", mbaixa: "muito baixa" };

/** Faixa de uma célula: { faixa: alta|media|baixa|mbaixa|zero, pct: 0–1 do pico, pouco }. */
export function faixaDoPico(valor, pico, pedidos) {
  const v = Number(valor) || 0;
  if (v <= 0 || !(pico > 0)) return { faixa: "zero", pct: 0, pouco: false };
  const pct = v / pico;
  let faixa = pct >= 0.85 ? "alta" : pct >= 0.5 ? "media" : pct >= 0.3 ? "baixa" : "mbaixa";
  const pouco = pedidos != null && pedidos < MIN_PEDIDOS_FORTE && (faixa === "alta" || faixa === "media");
  if (pouco) faixa = "baixa";
  return { faixa, pct, pouco };
}

export const DIAS_SEMANA = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const somarDias = (porDia, dias) => (porDia || []).reduce((s, n, d) => s + (!dias?.length || dias.includes(d) ? n : 0), 0);

/**
 * Hora a hora do Resumo só com os dias da semana marcados (0 = segunda; nenhum
 * marcado = todos). Monta as mesmas `horas` e `dias` que o backend manda somados.
 */
export function porHoraDosDias(porHora, dias) {
  if (!porHora || !dias?.length) return porHora;
  const horas = {};
  (porHora.celulas || []).forEach(([d, h, modulo, fat, pedidos]) => {
    if (!dias.includes(d)) return;
    const x = horas[h] || (horas[h] = { hora: h, rotulo: `${String(h).padStart(2, "0")}h`, total: 0, pedidos: { total: 0 } });
    x[modulo] = (x[modulo] || 0) + fat;
    x.total += fat;
    x.pedidos[modulo] = (x.pedidos[modulo] || 0) + pedidos;
    x.pedidos.total += pedidos;
  });
  return { ...porHora, horas: Object.values(horas).sort((a, b) => a.hora - b.hora), dias: somarDias(porHora.dias_semana, dias) };
}

/**
 * Mapa mesa × hora com os dias marcados: { porMesa: {mesa: {hora: {fat, min, abertas}}}, dias }.
 * `dias` = quantos dias de movimento entram na conta (para a % de ocupação).
 */
export function mapaMesasDosDias(mapa, dias) {
  const porMesa = {};
  (mapa?.celulas || []).forEach(([mesa, d, h, fat, min, abertas]) => {
    if (dias?.length && !dias.includes(d)) return;
    const m = porMesa[mesa] || (porMesa[mesa] = {});
    const c = m[h] || (m[h] = { fat: 0, min: 0, abertas: 0 });
    c.fat += fat;
    c.min += min;
    c.abertas += abertas;
  });
  return { porMesa, dias: somarDias(mapa?.dias_semana, dias) };
}

/** Real sem centavos para caber na célula: "R$ 6.612". O valor com centavos vai na dica. */
export const reaisInteiros = (v) => `R$ ${Math.round(Number(v) || 0).toLocaleString("pt-BR")}`;
